import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import LinkBadge from './LinkBadge';
import './DeckTile.css';

/**
 * Baustein — Deck-Tile.
 * Deck-Thumbnail mit 16:9-Art-Crop + Footer (Name + optional LinkBadge + Count + Action-Slot).
 * Fehlt das Bild oder schlägt es fehl (404) → Initial-Platzhalter aus dem Deck-Namen.
 *
 * Props:
 *   image      — URL zum Art-Crop (Hintergrundbild, center top)
 *   artist     — Künstler des Crops; steht mit dem ©-Vermerk als Streifen auf der Unterkante
 *                des Bildes. Scryfall verlangt beim Art-Crop die Quellenangabe in derselben
 *                Oberfläche (CLAUDE.md, Scryfall-Nutzungsregeln); ohne Künstler bleibt der
 *                ©-Vermerk, ohne Bild entfällt der Streifen.
 *   name       — Deck-Name
 *   count      — optional Anzahl Karten (Number oder String)
 *   link       — optional: Text einer Verknüpfung; rendert als LinkBadge unter dem Namen
 *   action     — optional: React-Node für Tile-Actions (z.B. <ContextMenu>)
 *   onClick    — Click-Handler; rendert ein unsichtbares, über die ganze Kachel
 *                gestrecktes Klick-Ziel als Geschwister des Inhalts. Die Kachel
 *                selbst bleibt ein <div>: im Footer liegen eigene Bedienelemente
 *                (Action-Slot), und ein umschließender Button wäre ein Button im
 *                Button (ungültiges HTML, kaputte Tastatur-/Screenreader-Bedienung).
 *   href       — optional: Ziel-URL. Das Klick-Ziel wird dann ein <a> statt eines
 *                <button>, womit Mittelklick, Strg-Klick und „In neuem Tab öffnen"
 *                funktionieren. Der normale Klick geht weiter an `onClick`.
 *   clickLabel — Name des Klick-Ziels für Screenreader; ohne Angabe der Deck-Name.
 *                Nur nötig, wenn der Klick etwas anderes tut als das Deck öffnen
 *                (z.B. „Zu Atraxa hinzufügen" im Karte-zu-Deck-Modal).
 *   selected   — boolean: Kachel ist die aktuelle Auswahl (Akzent-Rahmen + Ring + Häkchen-Chip
 *                oben rechts auf dem Bild, `aria-pressed="true"` am Klick-Ziel; ohne Auswahl
 *                fehlt das Attribut ganz). Der Hover bleibt
 *                unverändert — Hover und Auswahl sind zwei verschiedene Signale, deshalb trägt
 *                der Hover keine Akzentfarbe. Nicht gewählte Nachbarn werden nicht gedimmt.
 *   disabled   — nicht wählbar: gedimmt, kein Klick-Ziel, kein Hover-Lift; disabledLabel nennt
 *                den Grund (z.B. „bereits enthalten")
 *   disabledLabel — Text im Footer, wenn disabled gesetzt ist
 */
export default function DeckTile({ image, artist, name, count, link, action, onClick, href, clickLabel, selected = false, disabled = false, disabledLabel }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [image]);
  const showImage = !!image && !failed;
  const initial = ((name || '?').trim().charAt(0) || '?').toUpperCase();

  const clickable = !disabled && (!!onClick || !!href);

  const handleClick = (e) => {
    if (!onClick) return;
    // Modifier-Klick auf einem <a> gehört dem Browser (neuer Tab/Fenster) —
    // preventDefault würde ihn schlucken.
    if (href && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    e.preventDefault();
    onClick(e);
  };

  const clickProps = { className: 'deck-tile__click', onClick: handleClick };
  // `aria-pressed` nur bei Auswahl: ein `false` würde jede reine Navigations-Kachel
  // zum Umschalt-Knopf erklären.
  const buttonProps = { ...clickProps, 'aria-pressed': selected || undefined };
  // Das Klick-Ziel liegt durchsichtig über der Kachel und hat deshalb keinen
  // sichtbaren Text — der Name kommt als optisch verstecktes Label.
  const clickLabelNode = <span className="deck-tile__click-label">{clickLabel || name}</span>;

  const classes = ['deck-tile'];
  if (clickable) classes.push('deck-tile--clickable');
  if (selected) classes.push('deck-tile--selected');
  if (disabled) classes.push('deck-tile--disabled');

  return (
    <div className={classes.join(' ')}>
      {clickable && (href
        ? <a href={href} {...clickProps}>{clickLabelNode}</a>
        : <button type="button" {...buttonProps}>{clickLabelNode}</button>
      )}
      {showImage ? (
        <div className="deck-tile__image" style={{ backgroundImage: `url('${image}')` }}>
          <span className="deck-tile__credit">
            {artist && <span className="deck-tile__credit-artist">Illustration: {artist}</span>}
            <span className="deck-tile__credit-rights">© Wizards of the Coast</span>
          </span>
        </div>
      ) : (
        <div className="deck-tile__image deck-tile__image--placeholder">
          <span className="deck-tile__placeholder-letter">{initial}</span>
        </div>
      )}
      {selected && (
        <span className="deck-tile__selected-chip" aria-hidden>
          <Check size={14} />
        </span>
      )}
      {/* Versteckte Probe-Image erkennt 404 (background-image feuert kein onError). */}
      {!!image && !failed && (
        <img src={image} alt="" aria-hidden className="deck-tile__probe" onError={() => setFailed(true)} />
      )}
      <div className="deck-tile__footer">
        <div className="deck-tile__name-wrap">
          <span className="deck-tile__name">{name}</span>
          {link && <LinkBadge>{link}</LinkBadge>}
          {disabled && disabledLabel && <span className="deck-tile__disabled-label">{disabledLabel}</span>}
        </div>
        {count != null && <span className="deck-tile__count">{count}</span>}
        {action && <span className="deck-tile__action">{action}</span>}
      </div>
    </div>
  );
}
